import type { LonLat } from '@onxmaps/map-x/geographic'
import type { MapViewer } from '@onxmaps/map-x/viewer'
import type { EventEmitter, EventList } from '@onxmaps/map-x/events'
import type { ElementContext, ElementControls } from '@/lib/map-x/anyhtml/types'

/**
 * Update element position based on current map state
 *
 * Converts geographic coordinates to screen pixels and applies absolute positioning.
 *
 * @param context Element positioning context containing element, location, and map viewer
 */
function updateElementPosition(context: ElementContext): void {
  const { mapViewer, element, location } = context
  // Convert geographic coordinates to screen pixels
  const screenPoint = mapViewer.convertLonLatToCtxPoint(location)
  // Apply pixel positioning
  element.style.left = `${screenPoint[0]}px`
  element.style.top = `${screenPoint[1]}px`
  element.style.position = 'absolute'
}

/**
 * Creates a render handler for element
 *
 * Returns a function that updates element position on map events.
 * Automatically stops processing if element is removed from DOM.
 *
 * @param context Element positioning context
 * @returns Event handler function for map render events
 */
function createRenderHandler(context: ElementContext) {
  return () => {
    // Auto-cleanup: if element was removed externally, stop processing
    if (!document.contains(context.element)) {
      return
    }

    updateElementPosition(context)
  }
}

/**
 * Set an HTML element to a specific geographic location on the map and create a controller to manage it
 *
 * @param element The HTML element to position and control
 * @param location The geographic location [longitude, latitude] to position the element at
 * @param mapViewer The map viewer instance
 * @param eventEmitter An event emitter instance (e.g. an instance of EventsManager which is viewer and stylesheet aware is a type of EventEmitter<EventList>)
 * @param eventString Optional event name to listen for map updates (defaults to 'map-x-render')
 * @param bind Optional binder function for automatic variable nullification on destroy
 * @returns ElementControls for ongoing management of the positioned element
 *
 * ## Usage Patterns
 *
 * ### Pattern 1: Fire-and-forget (Framework Managed Lifecycle)
 * Use when your framework/component will handle cleanup automatically.
 * ```ts
 * // Simple positioning - framework cleans up when component unmounts
 * setHTMLElementToMapLocation(tooltipHTMLElement, [lng, lat], mapViewer, eventEmitter)
 * setHTMLElementToMapLocation(myComponentRef, [lng, lat], mapViewer, eventEmitter)
 * ```
 *
 * ### Pattern 2: Manual Lifecycle Without Auto-nullification
 * Use when you want control but don't need automatic variable management.
 * ```ts
 * // Get controller for manual management
 * const controller = setHTMLElementToMapLocation(popupHTMLElement, [lng, lat], mapViewer, eventEmitter)
 *
 * // Update position anytime
 * controller.setLocation([newLng, newLat])
 *
 * // Method chaining for multiple operations
 * controller.setLocation([lng1, lat1]).setLocation([lng2, lat2])
 *
 * // Temporarily stop updates but keep element visible
 * controller.cleanupRenderHandler()
 *
 * // Re-enable automatic updates
 * controller.restartRenderHandler()
 *
 * // Manual cleanup when done
 * controller.destroy()
 * ```
 *
 * ### Pattern 3: Auto-nullifying Lifecycle
 * Use when you want the variable to automatically become null after destruction.
 * ```ts
 * // Controller automatically becomes null after destroy()
 * let controller = null
 * controller = setHTMLElementToMapLocation(
 *   element,
 *   [lng, lat],
 *   mapViewer,
 *   eventEmitter,
 *   'map-x-render',
 *   next => (controller = next) // Binder for automatic variable management
 * )
 *
 * // Later: destroy() automatically sets controller = null
 * controller?.destroy() // Now controller === null
 *
 * // Safe to check without manual nullification
 * if (!controller) {
 *   // This condition will be true after destroy()
 * }
 * ```
 *
 * ### Pattern 4: Event-driven Lifecycle
 * Use for interactive elements that respond to user events.
 * ```ts
 * let controller = null
 *
 * canvas.addEventListener('click', (event) => {
 *   if (!controller) {
 *     // Create new controller with auto-nullification
 *     controller = setHTMLElementToMapLocation(
 *       element,
 *       [event.offsetX, event.offsetY],
 *       mapViewer,
 *       eventEmitter,
 *       'map-x-render',
 *       next => (controller = next)
 *     )
 *   } else {
 *     // Update existing position
 *     controller.setLocation([event.offsetX, event.offsetY])
 *   }
 * })
 *
 * canvas.addEventListener('mouseout', () => {
 *   controller?.destroy() // Auto-nullifies controller variable
 * })
 *
 * ```
 *
 * ### Error Handling
 * ```ts
 * try {
 *   const controller = setHTMLElementToMapLocation(element, [lng, lat], mapViewer, eventEmitter)
 * } catch (error) {
 *   // Handles: invalid element, invalid coordinates, missing mapViewer
 *   console.error('Failed to position element:', error.message)
 * }
 * ```
 *
 * ### Alternative: using the descriptive alias
 * ```
 * controller = setHTMLElementAndCreateController(element, coords, mapViewer, eventEmitter, binder)
 * ```
 */
export const setHTMLElementToMapLocation = (
  element: HTMLElement,
  location: LonLat,
  mapViewer: MapViewer,
  eventEmitter: EventEmitter<EventList>,
  eventString: keyof EventList = 'map-x-render',
  bind?: (next: ElementControls | null) => void
): ElementControls => {
  validateArgs(element, location, mapViewer, eventEmitter)

  const elementManager: ElementContext = {
    element,
    location,
    mapViewer,
  }

  const renderHandler = createRenderHandler(elementManager)

  // Initialize position and register event listener
  updateElementPosition(elementManager)
  eventEmitter.on(eventString, renderHandler)

  // Add to DOM if not already present
  const mapContainer = mapViewer.getCanvas().parentElement
  if (mapContainer && !mapContainer.contains(element)) {
    mapContainer.after(element)
  }

  // Create controller with bound factory pattern
  let destroyed = false
  let handlerActive = true

  const controller: ElementControls = {
    setLocation: (newLocation: LonLat) => {
      // no-op if destroyed
      if (destroyed) {
        return controller
      }
      elementManager.location = newLocation
      updateElementPosition({
        element: elementManager.element,
        location: newLocation,
        mapViewer,
      })
      // chainable
      return controller
    },
    cleanupRenderHandler: () => {
      if (destroyed || !handlerActive) {
        return
      }
      eventEmitter.off(eventString, renderHandler)
      handlerActive = false
    },
    restartRenderHandler: () => {
      if (destroyed || handlerActive) {
        return
      }
      eventEmitter.on(eventString, renderHandler)
      handlerActive = true
    },
    destroy: () => {
      if (destroyed) {
        return
      }
      destroyed = true

      // Clean up resources
      if (handlerActive) {
        eventEmitter.off(eventString, renderHandler)
      }
      elementManager.element.remove()

      // Auto-nullify external variable if binder was provided
      bind?.(null)
    },
    getElement: () => elementManager.element,
    getLocation: () => elementManager.location,
    get element() {
      return elementManager.element
    },
    get location() {
      return elementManager.location
    },
  }

  // Let the caller optionally receive the instance on creation
  bind?.(controller)

  return controller
}

/**
 * Validates input parameters for setting an elements location
 */
function validateArgs(
  element: HTMLElement,
  location: LonLat,
  mapViewer: MapViewer,
  eventEmitter: EventEmitter<EventList>
): void {
  if (!eventEmitter) {
    throw new Error(
      'EventEmitter is required for setting element location, recieved:',
      eventEmitter
    )
  }
  if (!mapViewer) {
    throw new Error(
      'Viewer is required for setting element location, received:',
      mapViewer
    )
  }
  if (!element) {
    throw new Error(
      'HTML element is required for setting location, received:',
      element
    )
  }
  if (!location || location.length !== 2) {
    throw new Error('Location must be [longitude, latitude] array')
  }
}

/**
 * Descriptive alias for setHTMLElementToMapLocation that emphasizes controller creation
 *
 * @example
 * ```ts
 * // Emphasizes that we want the controller for management
 * const controller = setHTMLElementAndCreateController(popup, [lng, lat], mapViewer, eventEmitter)
 *
 * // vs. the more general function name
 * setHTMLElementToMapLocation(popup, [lng, lat], mapViewer, eventEmitter)
 * ```
 *
 * @see setHTMLElementToMapLocation - Same function, different name for clarity
 */
export const setHTMLElementAndCreateController = setHTMLElementToMapLocation
